/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import EntityLink from '../../components/common/entity-link';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { personnelService } from '../../services/personnel.service';
import { fileUploadService } from '../../services/file-upload.service';
import type { Personnel } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { formatDate } from 'sharedUi/dateUtils';
import { formatPhone } from '../../utils/phone-display';

// --- Attributes rendering ---------------------------------------------------
// Personnel carry a free-form `attributes` object (designation, role[], batchNo,
// etc.). We surface it as plain key → value pairs, skipping raw MongoDB ObjectIds
// (e.g. a role's `roleid`) since those aren't human-readable — only the readable
// bits (like `roleName`) are shown.
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
const isObjectId = (v: unknown): boolean => typeof v === 'string' && OBJECT_ID_RE.test(v);

// camelCase / snake_case key -> "Title Case" label.
const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

// Keys already represented elsewhere on the screen — hide them from Attributes.
const ATTR_SKIP_KEYS = new Set(['profilePicUrl', 'profilepicurl']);

// Render one attribute value to a display string, dropping ObjectIds/empties.
const renderAttrValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (isObjectId(value)) return null;
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (item && typeof item === 'object') {
          const inner = Object.values(item)
            .filter((v) => !isObjectId(v) && v !== null && v !== undefined && v !== '')
            .map((v) => String(v));
          return inner.join(' ');
        }
        return isObjectId(item) ? '' : String(item ?? '');
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  if (typeof value === 'object') {
    const inner = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => !isObjectId(v) && v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${humanizeKey(k)}: ${v}`);
    return inner.length ? inner.join(', ') : null;
  }
  return String(value);
};

// A labelled read-only field that renders NOTHING when its value is empty — so the
// view only shows populated info instead of rows of "-".
const InfoField = ({ label, value }: { label: string; value?: ReactNode }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="overflow-hidden">
      <label className="text-xs text-ink-muted block mb-0.5">{label}</label>
      <p className="text-sm text-ink break-words">{value}</p>
    </div>
  );
};

const PersonnelView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'personnel',
    basePath: '/personnel',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.PERSONNEL);
  const canDelete = useCanDelete(JOB_NAMES.PERSONNEL);

  const [personnel, setPersonnel] = useState<Personnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

  // Set breadcrumb title to show personnel name instead of UUID
  useBreadcrumbTitle(personnel?.name || null);

  // Get createdBy/updatedBy names using cached lookup
  // const { createdByName, updatedByName } = useAuditNames(
  //   personnel?.createdBy,
  //   personnel?.updatedBy
  // );

  // Stable fetch function
  const fetchPersonnel = useCallback(async () => {
    try {
      setLoading(true);
      const res = await personnelService.getById(id!);
      setPersonnel(res);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to fetch personnel details'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch on mount or when ID changes
  useEffect(() => {
    if (!isReady || !id) return;
    fetchPersonnel();
  }, [isReady, id, fetchPersonnel]);

  // Fetch profile picture when personnel data is loaded
  useEffect(() => {
    let blobUrl: string | null = null;

    if (personnel?.picture) {
      fileUploadService.getFileUrl(personnel.picture)
        .then((url) => {
          blobUrl = url;
          setProfilePictureUrl(url);
        })
        .catch((err) => {
          console.error('Failed to fetch profile picture:', err);
          setProfilePictureUrl(null);
        });
    } else {
      setProfilePictureUrl(null);
    }

    // Cleanup blob URL
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [personnel?.picture]);

  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await personnelService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete personnel'));
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !personnel) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Personnel not found'} className="mb-4 w-full" />
        <Button
          label="Back to Personnel"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  const fullName = personnel.title
    ? `${personnel.title} ${personnel.name || ''}`.trim()
    : personnel.name || '-';

  return (
    <div className="p-3" data-testid="SCR-Personnel-View">
      {/* Header Section */}
      <ScreenHeader icon="pi pi-users" title="Personnel Details" onBack={() => navigateToList()} />

      {/* Profile Header with Edit/Delete */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          {/* Profile Picture */}
          {profilePictureUrl ? (
            <div className="w-12 h-12 rounded border border-gray-200 dark:border-gray-600 overflow-hidden bg-white flex-shrink-0">
              <img
                src={profilePictureUrl}
                alt={fullName}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-semibold text-gray-400">
                {(personnel.name || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">{fullName}</h2>
              {personnel.userId && (
                <span className="text-xs font-medium text-ink-muted bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {personnel.userId}
                </span>
              )}
              {personnel.rank?.name && (
                <Tag value={personnel.rank.name} severity="info" className="text-xs px-1.5 py-0.5" />
              )}
              {personnel.department?.name && (
                <Tag value={personnel.department.name} severity="secondary" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <p className="text-xs text-ink-muted">
              {personnel.email || '-'} {personnel.phoneNo && `• ${formatPhone(personnel.phoneNo)}`}
            </p>
          </div>
        </div>

        {/* Edit/Delete buttons aligned with profile */}
        {!(personnel as any).isDelete && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Personal Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Personal Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <InfoField label="User ID" value={personnel.userId} />
            <InfoField label="Full Name" value={fullName && fullName !== '-' ? fullName : undefined} />
            <InfoField label="Mobile" value={formatPhone(personnel.phoneNo)} />
            <InfoField label="Email" value={personnel.email} />
          </div>
        </div>

        {/* Professional Information Card — only rendered fields with values show,
            and the whole card is hidden if none are populated. */}
        {(() => {
          const unitId = personnel.unit?._id || (personnel as any).unitId;
          const unitLabel = personnel.unit?.name || (personnel as any).unitName;
          const deptId = personnel.department?._id || (personnel as any).departmentId;
          const deptLabel = personnel.department?.name;
          const ministryId = (personnel as any).ministry?._id || (personnel as any).ministryId;
          const ministryLabel = (personnel as any).ministry?.name;
          const desigId =
            (personnel as any).designation?._id ||
            (personnel as any).designationId ||
            personnel.units?.[0]?.designationId;
          const desigLabel =
            (personnel as any).designation?.name || personnel.units?.[0]?.designation?.name;
          const onDuty = (personnel as any).isOnDuty;
          const hasOnDuty = typeof onDuty === 'boolean';

          const hasAny =
            unitId || unitLabel || deptId || deptLabel || ministryId || ministryLabel ||
            desigId || desigLabel || hasOnDuty;
          if (!hasAny) return null;

          return (
            <div className="ds-card p-3">
              <h2 className="text-sm font-semibold text-ink mb-2">Professional Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {(unitId || unitLabel) && (
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Unit</label>
                    <EntityLink type="unit" id={unitId} label={unitLabel} />
                  </div>
                )}
                {(deptId || deptLabel) && (
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Department</label>
                    <EntityLink type="department" id={deptId} label={deptLabel} />
                  </div>
                )}
                {(ministryId || ministryLabel) && (
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Ministry</label>
                    <EntityLink type="ministry" id={ministryId} label={ministryLabel} />
                  </div>
                )}
                {(desigId || desigLabel) && (
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Designation</label>
                    <EntityLink type="designation" id={desigId} label={desigLabel} />
                  </div>
                )}
                {hasOnDuty && (
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">On Duty</label>
                    <p className="text-sm text-ink">{onDuty ? 'Yes' : 'No'}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Attributes Card - Full Width. Renders the free-form attributes object as
          key → value pairs, skipping raw ObjectIds. */}
      {(() => {
        const attrs = (personnel as any).attributes as Record<string, unknown> | undefined;
        if (!attrs || typeof attrs !== 'object') return null;
        const entries = Object.entries(attrs)
          .filter(([key]) => !ATTR_SKIP_KEYS.has(key.toLowerCase()))
          .map(([key, value]) => [key, renderAttrValue(value)] as const)
          .filter(([, rendered]) => rendered !== null && rendered !== '');
        if (!entries.length) return null;
        return (
          <div className="ds-card p-3 mt-2">
            <h2 className="text-sm font-semibold text-ink mb-2">Attributes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
              {entries.map(([key, rendered]) => (
                <div key={key}>
                  <label className="text-xs text-ink-muted block mb-0.5">
                    {humanizeKey(key)}
                  </label>
                  <p className="text-sm text-ink break-words">{rendered}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Post & Unit Assignments Card - Full Width */}
      {personnel.posts && personnel.posts.length > 0 && (
        <div className="ds-card p-3 mt-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-ink">Post & Unit Assignments</h2>
            <span className="text-xs text-ink-muted">
              {personnel.posts.length} assignment{personnel.posts.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-3">
            {personnel.posts.map((postAssignment, index) => (
              <div
                key={postAssignment.postId || index}
                className="border border-line rounded-lg p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-ink">
                      {postAssignment.postName}
                    </h3>
                    {postAssignment.isPrimary && (
                      <Tag value="Primary" severity="success" className="text-xs px-1.5 py-0.5" />
                    )}
                    {postAssignment.isUnitHead && (
                      <Tag value="Unit Head" severity="info" className="text-xs px-1.5 py-0.5" />
                    )}
                  </div>
                  <span className="text-xs text-ink-muted">
                    {postAssignment.assignmentType}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Post Code</label>
                    <p className="text-xs text-ink">{postAssignment.postCode}</p>
                  </div>
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Unit</label>
                    <p className="text-xs text-ink">{postAssignment.unitName}</p>
                  </div>
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">Start Date</label>
                    <p className="text-xs text-ink">
                      {formatDate(postAssignment.startDate)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-ink-muted block mb-0.5">End Date</label>
                    <p className="text-xs text-ink">
                      {!postAssignment.isPrimary && postAssignment.endDate && postAssignment.endDate !== ''
                        ? formatDate(postAssignment.endDate)
                        : postAssignment.isPrimary ? '-' : 'Current'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid for remaining cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
        {/* Unit Assignments Card */}
        {personnel.units && personnel.units.length > 0 && (
          <div className="ds-card p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-ink">Unit Assignments</h2>
              <span className="text-xs text-ink-muted">
                {personnel.units.length} unit{personnel.units.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-2">
              {personnel.units.map((unitAssignment, index) => (
                <div key={index} className="flex items-center justify-between py-1.5 border-b border-line last:border-0">
                  <div>
                    <EntityLink
                      type="unit"
                      id={unitAssignment.unit?._id || unitAssignment.unitId}
                      label={unitAssignment.unit?.name}
                      className="text-sm font-medium"
                    />
                    {(unitAssignment.designation?.name || unitAssignment.designationId) && (
                      <div className="mt-0.5">
                        <EntityLink
                          type="designation"
                          id={unitAssignment.designation?._id || unitAssignment.designationId}
                          label={unitAssignment.designation?.name}
                          className="text-xs"
                        />
                      </div>
                    )}
                  </div>
                  {index === 0 && (
                    <Tag value="Primary" severity="success" className="text-xs px-1.5 py-0.5" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Information */}
        <AuditInfoCard
          createdAt={personnel.createdAt}
          updatedAt={personnel.updatedAt}
          createdBy={personnel.createdBy}
          updatedBy={personnel.updatedBy}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete Personnel"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
        modal
        dismissableMask
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="pi pi-exclamation-triangle text-red-600" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{fullName}</strong>?
            </p>
            <p className="text-xs text-ink-muted mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PersonnelView;
